import { GameType } from '../persistence/types.js';
import { Position, Point } from '../core/GridGeometry.js';

/**
 * Elements that contribute to a game's score, for the Scoring-rules overlay.
 * Primary = the "× 100" elements (placed pieces / spread); secondary = the
 * additive elements (cells visited, corners, …). Cells highlight whole squares;
 * edges highlight a segment between two grid points. All fields optional — a game
 * provides whichever apply.
 */
export interface ScoringHighlight {
    primaryCells?: Position[];
    primaryEdges?: Array<[Point, Point]>;
    /** Amber dots at grid points (e.g. line corners). Fractional coords allowed. */
    primaryPoints?: Point[];
    secondaryCells?: Position[];
    secondaryEdges?: Array<[Point, Point]>;
    /** Blue dots at grid points (e.g. knight crossings). Fractional coords allowed. */
    secondaryPoints?: Point[];
    /**
     * "Spread" metric (min↔max digit distance/time): both digit value badges are
     * redrawn and joined by an arc. `primary` colors it amber (×100 metric, e.g.
     * branches distance spread) vs blue (additive, e.g. the time-spread games).
     * `badgeSize` should match the size the game draws its own NE badges at so the
     * overlay redraw is seamless (defaults to 'md').
     */
    spreadArc?: {
        from: Position;
        to: Position;
        fromValue: number;
        toValue: number;
        primary: boolean;
        badgeSize?: 'sm' | 'md' | 'lg';
    };
}

/**
 * One-line scoring rule per game, shown in the status bar by the Scoring-rules
 * overlay. Format mirrors the score: "<×100 element> × 100 + <additive element>".
 * (These replaced the old per-game "Scoring" tutorial scenes.)
 */
export const SCORING_RULES: Record<GameType, string> = {
    antennas: 'Antenna cells × 100 + empty cells visited by signals',
    branches: 'Distance spread × 100 + corners (excluding digits)',
    crossing: 'Teleporter pairs × 100 + empty cells visited by both balls',
    deluge: 'Diagonal walls × 100 + time spread',
    flood: 'Wall segments × 100 + time spread',
    ignition: 'Total fuse length × 100 + time spread',
    jumps: 'Launchers placed × 100 + ramp cells (excluding landings and launchers)',
    knight: 'Steps taken × 100 + segment crossings',
    line: 'Corners × 100 + segments',
    mirrors: 'Mirrors placed × 100 + empty cells visited by beam',
    orbits: 'Gates placed × 100 + empty cells visited by ship',
    splitting: 'Splitters placed × 100 + distance spread',
    turns: 'Turns placed × 100 + empty cells driven through',
    walls: 'Walls placed × 100 + empty cells visited by block',
};

/**
 * Render a scoring rule for the Scoring-overlay status line. The ×100 primary
 * term is parenthesised (and lower-cased, now mid-sentence) to mirror how the
 * score is actually computed: "Score: (A × 100) + B". Always closes with the
 * reminder that the score is a golf score.
 */
export function formatScoringRule(type: GameType): string {
    const [primary, secondary] = SCORING_RULES[type].split(' × 100 + ');
    const lowered = primary.charAt(0).toLowerCase() + primary.slice(1);
    return `Score: (${lowered} × 100) + ${secondary}. Lower is better!`;
}
